import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useOptionChainScreenContext } from "../context/OptionChainContext";
import { useTheme } from "../../../context/ThemeContext";

function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

const DEFAULT_SETTINGS = {
  showCalls: true,
  showPuts: true,
  showDelta: false,
  showTheta: false,
  showGamma: false,
  showVega: false,
  showRho: false,
  showOIChng: false,
  showOIBuilddup: false,
  noOfStrikes: 30,
};

export default function Configure({
  configureSettingsMap,
  setConfigureSettingsMap,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState(DEFAULT_SETTINGS);
  const originalSettingsRef = useRef(null);
  const ref = useRef(null);
  const { theme } = useTheme();

  //persisting no. of strikes:
  useEffect(() => {
    if (
      configureSettingsMap.maxStrikes > 0 &&
      configureSettingsMap.noOfStrikes > configureSettingsMap.maxStrikes
    ) {
      setConfigureSettingsMap((prev) => ({
        ...prev,
        noOfStrikes: Math.min(prev.noOfStrikes, prev.maxStrikes),
      }));
    }
  }, [configureSettingsMap.maxStrikes]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setLocalSettings(JSON.parse(JSON.stringify(configureSettingsMap)));
      originalSettingsRef.current = JSON.parse(
        JSON.stringify(configureSettingsMap)
      );
    }
  }, [isOpen]);

  const toggleSetting = (key) => {
    setLocalSettings((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateNoOfStrikes = (value) => {
    setLocalSettings((prev) => ({ ...prev, noOfStrikes: Number(value) }));
  };

  const handleReset = () => {
    setLocalSettings({ ...DEFAULT_SETTINGS });
  };

  const handleApply = () => {
    setConfigureSettingsMap({ ...localSettings });
    originalSettingsRef.current = JSON.parse(JSON.stringify(localSettings));
    setIsOpen(false);
  };

  const hasChanged =
    originalSettingsRef.current &&
    !deepEqual(localSettings, originalSettingsRef.current);

  return (
    <td className="ml-auto">
      <div className="relative" ref={ref}>
        <button
          className="text-[10px] px-6 py-2 bg-swipe-button text-p400 font-medium rounded flex items-center justify-center gap-2"
          onClick={() => setIsOpen(!isOpen)}>
          <p>Configure</p>
        </button>
        {isOpen && (
          <div className="absolute right-0 z-[9999] w-52 rounded-md shadow-lg bg-p400 border border-n200">
            <div className="px-4 py-3 border-b border-n200 flex justify-between items-center">
              <span className="text-[12px] text-p300 font-medium">
                Settings
              </span>
              <button
                className="text-[12px] font-semibold text-rose-600"
                onClick={handleReset}>
                Reset
              </button>
            </div>

            <div className="px-4 py-3 pt-1 border-b border-n200">
              <span className="text-[12px] text-p300 font-semibold">
                Display
              </span>
              <div className="mt-2 space-y-2">
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showCalls}
                    onChange={() => toggleSetting("showCalls")}
                  />{" "}
                  Call
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showPuts}
                    onChange={() => toggleSetting("showPuts")}
                  />{" "}
                  Put
                </label>
              </div>
            </div>

            <div className="px-4 py-3 pt-1 border-b border-n200">
              <span className="text-[12px] text-p300 font-semibold">
                Show/Hide Column
              </span>
              <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black mt-0.5"
                    checked={localSettings.showDelta}
                    onChange={() => toggleSetting("showDelta")}
                  />{" "}
                  Delta
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showTheta}
                    onChange={() => toggleSetting("showTheta")}
                  />{" "}
                  Theta
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showGamma}
                    onChange={() => toggleSetting("showGamma")}
                  />{" "}
                  Gamma
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showVega}
                    onChange={() => toggleSetting("showVega")}
                  />{" "}
                  Vega
                </label>
                <label className="flex items-center gap-2 text-[12px] font-medium text-n500">
                  <input
                    type="checkbox"
                    className="accent-black"
                    checked={localSettings.showRho}
                    onChange={() => toggleSetting("showRho")}
                  />{" "}
                  Rho
                </label>
              </div>
            </div>

            <div className="px-4 py-3">
              <div className="flex items-center justify-between mt-2">
                <div className="text-[12px] text-p300 font-semibold">
                  No. Of Strikes
                </div>
                <div className="text-center text-[10px] border-n200 text-p300 font-medium border rounded-sm px-1 py-0.5">
                  {localSettings.noOfStrikes}
                </div>
              </div>
              <input
                type="range"
                min="2"
                max={configureSettingsMap.maxStrikes}
                value={localSettings.noOfStrikes}
                onChange={(e) => updateNoOfStrikes(e.target.value)}
                className="w-full accent-black"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-p300 font-medium">2</span>
                <span className="text-[10px] text-p300 font-medium">
                  {configureSettingsMap.maxStrikes}
                </span>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-n200">
              <button
                disabled={!hasChanged}
                className={`text-[12px] py-2 font-semibold rounded flex gap-2 w-full justify-center items-center ${hasChanged ? "bg-swipe-button text-p400" : "bg-n500 text-white"}`}
                onClick={handleApply}>
                <img
                  src="/execute.svg"
                  alt="Execute icon"
                  className="w-4 h-4 mt-0.5"
                  style={{
                    filter:
                      theme === "dark" ? "brightness(0) invert(1)" : "none",
                  }}
                />
                <p>Apply</p>
              </button>
            </div>
          </div>
        )}
      </div>
    </td>
  );
}
